import type { TimelineItem } from "../types.js";

export interface ConvMeta {
  id: string;
  title: string;
  pinned: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface ConvMessage {
  id: string;
  role: "user" | "assistant" | "tool" | "status" | "result" | "thinking";
  text: string;
  kind?: string;
  url?: string;
  durationMs?: number;
  ts: number;
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(`${res.status}`);
  return (await res.json()) as T;
}

export const conversations = {
  list: () =>
    fetch("/api/conversations").then((r) => json<{ conversations: ConvMeta[] }>(r)).then((d) => d.conversations),

  create: (title?: string) =>
    fetch("/api/conversations", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ title }),
    }).then((r) => json<ConvMeta>(r)),

  get: (id: string) =>
    fetch(`/api/conversations/${id}`).then((r) =>
      json<{ meta: ConvMeta; messages: ConvMessage[] }>(r),
    ),

  update: (id: string, patch: { title?: string; pinned?: boolean }) =>
    fetch(`/api/conversations/${id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(patch),
    }).then((r) => json<ConvMeta>(r)),

  remove: (id: string) => fetch(`/api/conversations/${id}`, { method: "DELETE" }),

  appendMessage: (id: string, msg: { role: ConvMessage["role"]; text: string; kind?: string; url?: string }) =>
    fetch(`/api/conversations/${id}/messages`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(msg),
    }),
};

/** Map a stored message to a timeline item for rendering. */
export function messageToItem(m: ConvMessage): TimelineItem {
  const kind =
    m.role === "user"
      ? "you"
      : m.role === "assistant"
        ? "assistant"
        : m.role === "result"
          ? "result"
          : m.role === "tool"
            ? "tool"
            : m.role === "thinking"
              ? "thinking"
              : "status";
  return { id: m.id, kind, text: m.text, url: m.url, durationMs: m.durationMs, ts: m.ts };
}
